import { maxUrlRules, urlRuleValueMaxLength } from './constants.js';
import { currentPage, normalizePagePath } from './page.js';

/**
 * Mirror of WidgetUrlRules (Feedbax.Domain) — the two must stay in step. Where a surface may show
 * itself, decided here rather than on the server: the config response is one cache entry per site, so
 * asking it about a page would turn it into one per page. The loader knows the page anyway.
 *
 * One module for every surface that carries such a list (the bubbles, the trust corner, and whatever
 * comes next): a second copy of "does this page count" would drift from the first within a release.
 */

/**
 * One surface's rules from the config payload, validated and capped. Anything unexpected is dropped
 * rather than obeyed — a rule this loader misreads could silence a surface everywhere.
 * @param {object|null} config - Parsed config JSON, or a live settings message
 * @param {string} field - The payload field holding this surface's rules
 * @returns {Array<{mode: string, match: string, value: string}>}
 */
export function resolveUrlRules(config, field) {
    const raw = config ? config[field] : null;
    if (!Array.isArray(raw)) {
        return []; // Missing on an older payload: no rules, so every page counts — today's behaviour.
    }

    const result = [];
    for (const item of raw) {
        if (!item || typeof item.value !== 'string') {
            continue;
        }

        // An unknown word falls back to the harmless reading in both slots: a rule that does not
        // block, matched the widest way — so a payload from a newer server never blacks a surface
        // out on a page its author never named.
        const match = item.match === 'contains' || item.match === 'exact' ? item.match : 'startswith';
        const value = normalizeRuleValue(item.value, match);
        if (!value || value.length > urlRuleValueMaxLength) {
            continue;
        }

        result.push({
            mode: item.mode === 'block' ? 'block' : 'allow',
            match: match,
            value: value
        });

        if (result.length === maxUrlRules) {
            break;
        }
    }

    return result;
}

/**
 * Mirror of WidgetUrlRules.NormalizeValue: no scheme, no fragment, no trailing slash on the path,
 * lowercase, and no query except for a contains rule, the one reading that also sees the page's query.
 * The server normalizes on save, so this only catches a hand-edited or older value.
 * @param {string} value - The rule text as it arrived
 * @param {string} match - The rule's validated match word
 * @returns {string}
 */
export function normalizeRuleValue(value, match) {
    let text = typeof value === 'string' ? value.trim() : '';

    for (const scheme of ['https://', 'http://', '//']) {
        if (text.slice(0, scheme.length).toLowerCase() === scheme) {
            text = text.slice(scheme.length);
            break;
        }
    }

    const cut = match === 'contains' ? text.indexOf('#') : text.search(/[?#]/);
    if (cut >= 0) {
        text = text.slice(0, cut);
    }

    const queryAt = text.indexOf('?');
    const query = queryAt >= 0 ? text.slice(queryAt) : '';
    let pathPart = queryAt >= 0 ? text.slice(0, queryAt) : text;

    // The page's own path never ends in a slash, so neither may the rule's, query or not.
    while (pathPart.length > 1 && pathPart.charAt(pathPart.length - 1) === '/') {
        pathPart = pathPart.slice(0, -1);
    }

    return (pathPart + query).trim().toLowerCase();
}

/**
 * Whether one rule covers the given page. Compared against the host-and-path form AND against the bare
 * path, so both ways of writing a rule work: "beispiel.de/preise" names one page of one domain,
 * "/preise" the same page on every domain this widget sits on. A contains rule also sees the query,
 * appended to the host-and-path form.
 * @param {{match: string, value: string}} rule - One validated rule
 * @param {string} hostAndPath - The page as host + normalized path
 * @param {string} path - The page's normalized path
 * @param {string} query - The page's lowercase query with its "?", or ""
 * @returns {boolean}
 */
function ruleMatches(rule, hostAndPath, path, query) {
    if (rule.match === 'contains') {
        return (hostAndPath + query).indexOf(rule.value) >= 0;
    }

    if (rule.match === 'exact') {
        // A stored value never ends in a slash, the host-and-path form of a start page does
        // ("shop.beispiel.de/"), so a bare host is compared against the root with the slash added back.
        return path === rule.value
            || hostAndPath === rule.value
            || (path === '/' && hostAndPath === rule.value + '/');
    }

    return hostAndPath.indexOf(rule.value) === 0 || path.indexOf(rule.value) === 0;
}

/**
 * Whether a surface carrying these rules may show itself on the page the visitor is on right now. The
 * order is the whole contract: one allow rule anywhere confines the surface to what the allow rules
 * name, and a matching block rule still wins over that. No rules means every page.
 * @param {Array<{mode: string, match: string, value: string}>} rules - This surface's rules
 * @returns {boolean}
 */
export function urlRulesAllow(rules) {
    if (!Array.isArray(rules) || rules.length === 0) {
        return true;
    }

    const page = currentPage();
    const path = normalizePagePath(page.pathname);
    const hostAndPath = page.host.toLowerCase() + path;
    // location.search is "" for a bare "?", like the server's QueryOf.
    const query = page.search.toLowerCase();
    let hasAllowRule = false;
    let allowed = false;

    for (const rule of rules) {
        const matches = ruleMatches(rule, hostAndPath, path, query);

        if (rule.mode === 'block') {
            if (matches) {
                return false; // A block rule is final — the exception the provider carved out.
            }

            continue;
        }

        hasAllowRule = true;
        allowed = allowed || matches;
    }

    return !hasAllowRule || allowed;
}
